import React, { useEffect, useRef, useState } from 'react';
import { fetchSignInMethodsForEmail } from 'firebase/auth';
import { auth } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useErrorReport } from '../../contexts/ErrorReportContext';
import { getUserProfile, isUserDisabled } from '../../services/userProfile';
import GoogleIcon from './GoogleIcon';
import PasswordField from './PasswordField';
import {
  PASSWORD_RESET_SENT_MESSAGE,
  passwordResetOutcome,
} from './passwordResetOutcome';

const UNKNOWN_EMAIL_MESSAGE =
  "Email doesn’t exist — perhaps you need to create an account.";

async function isUnknownEmail(email) {
  try {
    const methods = await fetchSignInMethodsForEmail(auth, email.trim());
    return !methods || methods.length === 0;
  } catch {
    return false;
  }
}

function getErrorMessage(code) {
  switch (code) {
    case 'auth/user-not-found':
      return UNKNOWN_EMAIL_MESSAGE;
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Invalid email or password.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again later.';
    case 'auth/user-disabled':
      return 'This account has been disabled.';
    case 'auth/popup-blocked':
      return 'Pop-up blocked. Allow pop-ups and try again.';
    case 'auth/account-exists-with-different-credential':
      return 'An account with this email already exists. Sign in with email/password.';
    default:
      return 'Login failed. Please try again.';
  }
}

/**
 * Shared email/password + Google sign-in form.
 * @param {{ onSuccess: (uid: string, profile?: object | null) => void | Promise<void>; onRequestSignup: () => void; idPrefix?: string; autoFocus?: boolean }} props
 */
const SignInForm = ({
  onSuccess,
  onRequestSignup,
  idPrefix = '',
  autoFocus = true,
}) => {
  const { signIn, signInWithGoogle, signOut, sendPasswordReset } = useAuth();
  const { reportError } = useErrorReport();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [unknownEmail, setUnknownEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('signin');
  const [resetSent, setResetSent] = useState(false);
  const emailRef = useRef(null);

  const emailId = `${idPrefix}email`;
  const passwordId = `${idPrefix}password`;

  useEffect(() => {
    if (step === 'reset' && !resetSent) {
      emailRef.current?.focus();
    }
  }, [step, resetSent]);

  const finishAuth = async (uid) => {
    const profile = await getUserProfile(uid);
    if (isUserDisabled(profile)) {
      setUnknownEmail(false);
      setError('This account has been disabled. Ask an admin to Enable it in Users.');
      try {
        await signOut();
      } catch (_) {
        /* ignore */
      }
      return;
    }
    await onSuccess(uid, profile);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setUnknownEmail(false);
    setLoading(true);
    try {
      const cred = await signIn(email, password);
      await finishAuth(cred.user.uid);
    } catch (err) {
      console.error('Login error:', err);
      const code = err.code;
      if (code === 'auth/user-not-found') {
        setUnknownEmail(true);
        setError(UNKNOWN_EMAIL_MESSAGE);
      } else if (code === 'auth/invalid-credential' || code === 'auth/wrong-password') {
        const missing = await isUnknownEmail(email);
        if (missing) {
          setUnknownEmail(true);
          setError(UNKNOWN_EMAIL_MESSAGE);
        } else {
          setError('Invalid email or password.');
        }
      } else {
        setError(getErrorMessage(code));
      }
    } finally {
      setLoading(false);
    }
  };

  const openReset = () => {
    setError('');
    setUnknownEmail(false);
    setResetSent(false);
    setStep('reset');
  };

  const backToSignIn = () => {
    setError('');
    setUnknownEmail(false);
    setResetSent(false);
    setStep('signin');
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    setUnknownEmail(false);
    setLoading(true);
    try {
      await sendPasswordReset(email);
      setResetSent(true);
    } catch (err) {
      const outcome = passwordResetOutcome(err?.code);
      if (outcome.kind === 'sent') {
        setResetSent(true);
        return;
      }
      setResetSent(false);
      setError(outcome.message);
      if (outcome.kind === 'retry') {
        reportError(err, {
          pageLabel: 'Sign in',
          source: 'auth-password-reset',
          userMessage: outcome.message,
          silent: true,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError('');
    setUnknownEmail(false);
    setLoading(true);
    try {
      const cred = await signInWithGoogle();
      await finishAuth(cred.user.uid);
    } catch (err) {
      console.error('Google login error:', err);
      if (err.code === 'auth/popup-closed-by-user') {
        setError('');
      } else {
        setError(getErrorMessage(err.code));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {error && (
        <div className="login-error">
          <span>{error}</span>
          {unknownEmail ? (
            <>
              {' '}
              <button
                type="button"
                className="login-error__link"
                onClick={onRequestSignup}
              >
                Create an account
              </button>
            </>
          ) : null}
        </div>
      )}

      <button
        type="button"
        className="login-google-btn"
        onClick={handleGoogle}
        disabled={loading}
      >
        <GoogleIcon />
        Continue with Google
      </button>

      <div className="login-divider pub-mono" aria-hidden="true">
        <span>or</span>
      </div>

      {step === 'reset' ? (
        <form onSubmit={handleReset} className="login-form">
          {resetSent ? (
            <p className="login-reset-note" role="status">
              {PASSWORD_RESET_SENT_MESSAGE}
            </p>
          ) : (
            <div className="login-field">
              <label htmlFor={emailId}>Email</label>
              <input
                ref={emailRef}
                id={emailId}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
              />
            </div>
          )}

          {resetSent ? null : (
            <button type="submit" className="login-submit-btn" disabled={loading}>
              {loading ? 'Sending...' : 'Send reset link'}
            </button>
          )}

          <button type="button" className="login-text-btn" onClick={backToSignIn}>
            Back to sign in
          </button>
        </form>
      ) : (
        <form onSubmit={handleSubmit} className="login-form">
          <div className="login-field">
            <label htmlFor={emailId}>Email</label>
            <input
              id={emailId}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoFocus={autoFocus}
              autoComplete="email"
            />
          </div>

          <PasswordField
            id={passwordId}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
            autoComplete="current-password"
          />

          <button type="button" className="login-text-btn" onClick={openReset}>
            Forgot password?
          </button>

          <button type="submit" className="login-submit-btn" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      )}
    </>
  );
};

export default SignInForm;
